import { useCallback, useEffect, useState } from "react";
import { Images, Sparkles, Loader2, Download, Trash2, FilePlus2, X } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

function AuthImg({ id, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/ai-images/${id}/file`, { responseType: "blob" }).then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);
  if (!src) return <div className={`flex items-center justify-center bg-slate-100 ${className}`}><Loader2 size={18} className="animate-spin text-slate-300" /></div>;
  return <img src={src} alt={alt} className={className} />;
}

const IDEAS = [
  "A premium fintech hero image: abstract gold and deep-navy gradient with subtle geometric grid, no text",
  "A confident small-business owner in a modern office, warm cinematic lighting, editorial photography",
  "Clean 3D illustration of a rising credit score gauge in navy and gold, minimal background",
];

export default function ImageStudio() {
  const [data, setData] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null);
  const [pickFor, setPickFor] = useState(null);
  const [pages, setPages] = useState(null);
  const load = useCallback(() => { api.get("/ai-images/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Describe the image you want.");
    setBusy(true); setLatest(null);
    try { const { data } = await api.post("/ai-images/generate", { prompt }); setLatest(data); toast.success("Image generated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Generation failed."); } finally { setBusy(false); }
  };
  const download = async (id) => {
    try { const { data } = await api.get(`/ai-images/${id}/file`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `mmt-image-${id}.png`; a.click(); }
    catch { toast.error("Could not download."); }
  };
  const remove = async (id) => { try { await api.delete(`/ai-images/${id}`); if (latest?.id === id) setLatest(null); load(); } catch { toast.error("Could not delete."); } };
  const openPicker = async (id) => {
    setPickFor(id); setPages(null);
    try { const { data } = await api.get("/content/overview"); setPages(data.pages); }
    catch { toast.error("Could not load pages."); setPickFor(null); }
  };
  const addToPage = async (pageId) => {
    try { await api.post(`/content/pages/${pageId}/insert-image`, { image_id: pickFor }); toast.success("Image added to page as a new draft version"); setPickFor(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add image."); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="image-studio-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Images size={22} className="text-[#b0862f]" /> AI Image Studio</h1>
        <p className="mt-1 text-[13px] text-slate-500">Generate on-brand marketing &amp; website images with {data.model}. Images are saved to your library.</p>
      </div>

      <Card className="p-5">
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} data-testid="image-prompt"
          className="w-full rounded-lg border border-slate-200 p-3 text-[14px] outline-none focus:border-[#1a5fd0]" placeholder="Describe the image… e.g. A premium fintech hero image, gold-on-navy gradient, abstract geometric grid, no text" />
        <div className="mt-2 flex flex-wrap gap-2">
          {IDEAS.map((i, n) => <button key={n} onClick={() => setPrompt(i)} className="rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-600 hover:bg-slate-200">{i.slice(0, 42)}…</button>)}
        </div>
        <button onClick={generate} disabled={busy} data-testid="generate-image-btn" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {busy ? "Generating…" : "Generate image"}
        </button>
      </Card>

      {latest && (
        <Card className="overflow-hidden p-0" data-testid="latest-image">
          <AuthImg id={latest.id} alt={latest.prompt} className="max-h-[460px] w-full object-contain bg-slate-50" />
          <div className="flex items-center justify-between gap-3 p-4">
            <p className="text-[13px] text-slate-600">{latest.prompt}</p>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => openPicker(latest.id)} data-testid="add-to-page-latest" className="inline-flex items-center gap-1.5 rounded-lg bg-[#b0862f] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90"><FilePlus2 size={13} /> Add to page</button>
              <button onClick={() => download(latest.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-200"><Download size={13} /> Download</button>
            </div>
          </div>
        </Card>
      )}

      <div>
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Your library ({data.count})</div>
        {data.images.length === 0 ? (
          <Card className="p-8 text-center text-[13px] text-slate-400">No images yet. Generate your first above.</Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {data.images.map((im) => (
              <Card key={im.id} className="group overflow-hidden p-0" data-testid="library-image">
                <AuthImg id={im.id} alt={im.prompt} className="aspect-square w-full object-cover" />
                <div className="flex items-center justify-between gap-1 p-2">
                  <span className="truncate text-[11px] text-slate-500" title={im.prompt}>{im.prompt}</span>
                  <div className="flex shrink-0 gap-1.5">
                    <button onClick={() => openPicker(im.id)} data-testid="add-to-page-lib" title="Add to a content page" className="text-slate-400 hover:text-[#b0862f]"><FilePlus2 size={13} /></button>
                    <button onClick={() => download(im.id)} className="text-slate-400 hover:text-[#1a5fd0]"><Download size={13} /></button>
                    <button onClick={() => remove(im.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {pickFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="page-picker-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setPickFor(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-head text-lg font-extrabold text-slate-900">Add image to a page</h3><button onClick={() => setPickFor(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
            {!pages ? <Loader2 size={18} className="mx-auto my-6 animate-spin text-slate-300" /> : pages.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-slate-400">No content pages yet. Create one in Content Studio first.</p>
            ) : (
              <div className="max-h-80 space-y-1.5 overflow-y-auto">
                {pages.map((p) => (
                  <button key={p.id} onClick={() => addToPage(p.id)} data-testid="picker-page-row" className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:border-[#b0862f] hover:bg-[#b0862f]/5">
                    <span className="font-head text-[14px] font-bold text-slate-900">{p.title}</span>
                    <span className="font-mono text-[11px] text-slate-400">/{p.slug}</span>
                  </button>
                ))}
              </div>
            )}
            <p className="mt-3 text-[11px] text-slate-400">The image is appended as a new draft version. Review &amp; publish in Content Studio.</p>
          </div>
        </div>
      )}
    </div>
  );
}
